import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { useAccount } from '@/domain/hooks/useAccount'
import { Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { getTradeType } from '@/features/trade/TradeButton'
import { TradeForm } from '@/features/trade/components/form/TradeForm'
import { useTrade } from '@/features/trade/logic/useTrade'
import { SuccessView } from '@/features/trade/views/SuccessView'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { useCallback, useEffect, useState } from 'react'
import { useChainId, usePublicClient, useWalletClient } from 'wagmi'
import { TradeRouteInfo } from '../../trade/views/TradeRouteInfo'
import { swap } from '../logic/swap'
import { GasLimitExceededModal } from './GasLimitExceededModal'

interface TradePanelProps {
  withHeading?: boolean
  simplified?: boolean
  isModal?: boolean
  isSwapPage?: boolean
  token?: Token
  lockToToken?: boolean
  stackedSuccessScreen?: boolean
}

export function TradePanel({
  withHeading,
  simplified,
  isModal,
  isSwapPage,
  token,
  lockToToken,
  stackedSuccessScreen,
}: TradePanelProps) {
  const chainId = useChainId()
  const [slippage, setSlippage] = useState(Percentage(0.005)) // 0.5% default
  const [errorMessage, setErrorMessage] = useState<string>('')
  const nativeAssetInfo = getNativeAssetInfo(chainId)
  const trade = useTrade('Market', isSwapPage ? 'swap' : isModal ? 'modal' : 'trade', token, slippage.toNumber())

  // For swap execution in the gas limit modal
  const { data: walletClient } = useWalletClient()
  const publicClient = usePublicClient()
  const account = useAccount()
  const [swapState, setSwapState] = useState('')
  const [swapError, setSwapError] = useState(false)

  const { isL1Trade, isEvmTrade, isTransferAfter } = getTradeType(
    trade.fromTokenField.selectedAsset,
    trade.toTokenField.selectedAsset,
    nativeAssetInfo.nativeAssetSymbol,
    nativeAssetInfo.wrappedNativeAssetSymbol,
    trade.tradingWithAssetsSymbols,
  )

  // Execute swap for the big blocks flow in the modal
  const executeSwap = useCallback(async (): Promise<boolean> => {
    if (!account || !walletClient || !publicClient) {
      return false
    }

    const fromToken = trade.fromTokenField.selectedAsset?.token
    const toToken = trade.toTokenField.selectedAsset?.token
    const fromValue = trade.fromToken.value

    if (!fromToken || !toToken || !fromValue.gt(0)) {
      return false
    }

    const slippageBPS = Math.round(slippage.toNumber() * 10_000)
    let success = false

    await swap({
      inputToken: fromToken,
      outputToken: toToken,
      inputAmount: fromToken.toBaseUnit(fromValue),
      priceData: trade.swapQuoteData,
      walletConfig: {
        account,
        publicClient,
        walletClient,
      },
      chainId,
      nativeAssetSymbol: nativeAssetInfo.nativeAssetSymbol,
      slippage: slippageBPS,
      isUsingBigBlocks: true, // Always true when called from big blocks flow
      setSwapState,
      setSwapError,
      setQuoteError: (isError) => {
        setSwapError(isError)
      },
      onSuccess: () => {
        success = true
      },
    })

    return success
  }, [
    account,
    walletClient,
    publicClient,
    trade.fromTokenField.selectedAsset?.token,
    trade.toTokenField.selectedAsset?.token,
    trade.fromToken.value,
    trade.swapQuoteData,
    slippage,
    chainId,
    nativeAssetInfo.nativeAssetSymbol,
  ])

  // Clear error when user changes tokens, amounts, or slippage
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (errorMessage) {
      setErrorMessage('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trade.fromToken.token.symbol, trade.toToken.token.symbol, trade.fromToken.value.toString(), slippage.toString()])

  if (trade.pageStatus.state === 'success') {
    return (
      <SuccessView
        runConfetti={false}
        fromToken={trade.pageStatus.successData!.from}
        toToken={trade.pageStatus.successData!.to}
        resetForm={trade.pageStatus.resetForm}
        isModal={isModal ?? false}
        stackedSuccessScreen={stackedSuccessScreen}
      />
    )
  }

  return (
    <div className="relative w-full max-w-xl">
      {withHeading && (
        <Typography variant="h1" className="mb-7 pb-4 lg:text-4xl" gradient>
          Swap
        </Typography>
      )}

      <Wrapper simplified={simplified} withHeading={withHeading}>
        <TradeForm
          {...trade}
          isTransferAfter={isTransferAfter}
          isL1Trade={isL1Trade}
          isEvmTrade={isEvmTrade}
          setErrorMessage={setErrorMessage}
          slippage={slippage}
          currentPage="swap"
          activeTab="Market"
          lockToToken={lockToToken}
        />

        {errorMessage && (
          <div className="mt-4 rounded-lg border border-red-500/20 bg-red-500/10 p-3">
            <Typography variant="p" className="text-red-400 text-sm">
              {errorMessage}
            </Typography>
          </div>
        )}

        <TradeRouteInfo {...trade} slippage={slippage} setSlippage={setSlippage} />
      </Wrapper>

      {/* Gas Limit Exceeded Modal */}
      <GasLimitExceededModal
        open={trade.showGasLimitModal}
        onOpenChange={trade.setShowGasLimitModal}
        gasLimitDetails={trade.gasLimitDetails}
        slippage={slippage}
        setSlippage={setSlippage}
        isUsingBigBlocks={trade.isUsingBigBlocks}
        isHypercoreUser={trade.isHypercoreUser}
        executeSwap={executeSwap}
        swapState={swapState}
        swapError={swapError}
        onSwapSuccess={trade.pageStatus.goToSuccessScreen}
      />
    </div>
  )
}

function Wrapper({
  simplified,
  withHeading,
  children,
}: { simplified?: boolean; withHeading?: boolean; children: React.ReactNode }) {
  if (simplified) {
    return <div>{children}</div>
  }

  return (
    <Panel.Wrapper className={cn('flex flex-col gap-4 p-4 pb-8 md:p-6 md:pb-8', !withHeading && 'p-2 pt-0')}>
      {children}
    </Panel.Wrapper>
  )
}
